import type { Id } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { loadStoreResolver, type StoreView } from '../../data/stores';
import { db } from '../../db/db';

const shortDate = (ms: number) => new Date(ms).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });

function listy(n: number): string {
  if (n === 0) return 'bez list';
  if (n === 1) return '1 lista';
  const d = n % 10;
  const t = n % 100;
  return `${n} ${d >= 2 && d <= 4 && (t < 12 || t > 14) ? 'listy' : 'list'}`;
}

const nameKey = (s: StoreView) => (s.name ?? '').trim().toLocaleLowerCase('pl-PL');

/**
 * Opis sklepu pod nazwą: sieć, adres, ostatnia wizyta („ostatnio 11.10”).
 * Sklepy o tej samej nazwie dostają też liczbę list, żeby dało się je odróżnić (np. przed połączeniem).
 */
export function useStoreDetails(stores: StoreView[] | undefined): (store: StoreView) => string {
  const counts = useLiveQuery(async () => {
    const [lists, resolve] = await Promise.all([db.lists.toArray(), loadStoreResolver()]);
    const out = new Map<Id, number>();
    for (const l of lists) {
      if (l.deleted || !l.storeId) continue;
      const id = resolve(l.storeId);
      out.set(id, (out.get(id) ?? 0) + 1);
    }
    return out;
  });
  const seen = new Map<string, number>();
  for (const s of stores ?? []) seen.set(nameKey(s), (seen.get(nameKey(s)) ?? 0) + 1);

  return (s) => {
    const parts = [s.chainName, s.address, s.lastVisitMs ? `ostatnio ${shortDate(s.lastVisitMs)}` : null];
    if ((seen.get(nameKey(s)) ?? 0) > 1 && counts) parts.push(listy(counts.get(s.id) ?? 0));
    return parts.filter(Boolean).join(' · ');
  };
}
